// React and hooks
import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";

// React Router and i18n and date formatting
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Redux
import { useSelector } from "react-redux";

import _ from "lodash";
import debounce from "lodash/debounce";
import { motion, useAnimation } from "framer-motion";

// MUI Components
import {
  Dialog,
  DialogTitle,
  DialogContent,
  AppBar,
  Toolbar,
  Button,
  Container,
  Typography,
  Box,
  IconButton,
  Stack,
  Menu,
  MenuItem,
  Card,
  Alert,
  useMediaQuery,
} from "@mui/material";

// MUI Icons
import MenuIcon from "@mui/icons-material/Menu";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import WestOutlinedIcon from "@mui/icons-material/WestOutlined";
import EastOutlinedIcon from "@mui/icons-material/EastOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";
import Badge from "@mui/material/Badge";

// Context
import { AuthenticationContext } from "@cloudmlm/cloudmlm-auth";

// Redux Selectors
import {
  login,
  menuItemList,
  menuList,
  selectMenuItemList,
  selectMenuList,
  selectUserDetail,
  userDetail,
} from "../redux/slice/userSlice";

// i18n setup
import i18n from "../i18n";
import store from "../redux/store";
import InAppNotifications from "./InAppNotifications/InAppNotifications";
import SnackState from "./snackbar/snackState";
import CommonSnackBar from "./snackbar/commonSnackbar";

const locales = {
  en: { title: "English" },
  es: { title: "Spanish" },
};

const MotionAppBar = motion.create(AppBar);

function Header() {
  const { t } = useTranslation();
  const getAllMenus = useSelector(selectMenuList);
  const controls = useAnimation();
  const getAllItemMenus = useSelector(selectMenuItemList);
  const getLoggedUserDetails = useSelector(selectUserDetail);
  const navigate = useNavigate();
  const location = useLocation();
  const scrollContainerRef = useRef(null);
  const pathname = location.pathname;
  const currentLang = i18n.language;
  const { logUserOut, impersonateEmail } = useContext(AuthenticationContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedSubmenuItems, setSelectedSubmenuItems] = useState([]);
  const [anchorElLanguage, setAnchorElLanguage] = useState(null);
  const [anchorElProfile, setAnchorElProfile] = useState(null);
  const [getMyAccount, setGetMyAccount] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isUpgradePopupOpen, setIsUpgradePopupOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width:576px)");
  const lastScrollyRef = useRef(0);
  
  /* Start: Menu - Mouse scrollable */
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const scrollStartX = useRef(0);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    dragStartX.current = e.clientX;
    scrollStartX.current = scrollContainerRef.current.scrollLeft;
    document.body.style.userSelect = "none";
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    const dx = e.clientX - dragStartX.current;
    scrollContainerRef.current.scrollLeft = scrollStartX.current - dx;
    checkScrollButtons();
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    document.body.style.userSelect = "";
  };

  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const el = scrollContainerRef.current;
    el.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      el.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);
  /* End: Menu - Mouse scrollable */

  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [snack, closeSnack, showSnackbar] = SnackState();

  const handleScrollHeader = () => {
    const currentScrollY = window.scrollY;

    if (!isMobile) {
      if (currentScrollY > lastScrollyRef.current && currentScrollY > 60) {
        controls.start({ y: "-100%" }); // Hide
      } else {
        controls.start({ y: 0 }); // Show
      }
    }

    lastScrollyRef.current = currentScrollY;
  };

  useEffect(() => {
    const debouncedScroll = debounce(handleScrollHeader, 100);
    window.addEventListener("scroll", debouncedScroll);
    return () => window.removeEventListener("scroll", debouncedScroll);
  }, []);

  useEffect(() => {
    if (getAllItemMenus?.length > 0) {
      let getSelectedMenuId = [];
      let getSelectedSubmenuMenuId = [];
      const splitPath = pathname.split("/")?.filter(Boolean);
      if (splitPath[0] === "mediaPage") {
        try {
          const getMediaId = parseInt(atob(splitPath[1]));
          const getMediaParentId = getAllMenus.find(
            (item) => item.id === getMediaId
          );
          if (getMediaParentId !== undefined) {
            getSelectedMenuId.push(getMediaParentId.ParentId);
          }
        } catch (err) {
          navigate("/mediaPage");
        }
      } else if (splitPath[0] === "manageMembership") {
        const getParentMenu = getAllItemMenus.find(
          (item) => item.navLink === "/membership"
        );
        if (getParentMenu !== undefined) {
          getSelectedMenuId.push(getParentMenu.ParentId);
          getSelectedSubmenuMenuId.push(getParentMenu.id);
        }
      } else if (splitPath[0] === "vieworder") {
        const getParentMenu = getAllItemMenus.find(
          (item) => item.navLink === "/order"
        );
        if (getParentMenu !== undefined) {
          getSelectedMenuId.push(getParentMenu.ParentId);
          getSelectedSubmenuMenuId.push(getParentMenu.id);
        }
      } else if (splitPath[0] === "withDrawFunds") {
        const getParentMenu = getAllItemMenus.find(
          (item) => item.navLink === "/iWallet"
        );
        if (getParentMenu !== undefined) {
          getSelectedMenuId.push(getParentMenu.ParentId);
          getSelectedSubmenuMenuId.push(getParentMenu.id);
        }
      } else if (splitPath[0] === "manageProjects") {
        const getParentMenu = getAllItemMenus.find(
          (item) => item.navLink === "/projects"
        );
        if (getParentMenu !== undefined) {
          getSelectedMenuId.push(getParentMenu.ParentId);
        }
      } else if (splitPath[0] === "manageAffiliateProjects") {
        const getParentMenu = getAllItemMenus.find(
          (item) => item.navLink === "/affiliateProjects"
        );
        if (getParentMenu !== undefined) {
          getSelectedMenuId.push(getParentMenu.ParentId);
        }
      } else {
        const getParentMenu = getAllItemMenus.find(
          (item) => item.navLink === pathname
        );
        if (getParentMenu !== undefined) {
          getSelectedMenuId.push(getParentMenu.ParentId);
        }
      }
      setSelectedItems(getSelectedMenuId);
      setSelectedSubmenuItems(getSelectedSubmenuMenuId);
    }
  }, [pathname]);

  useEffect(() => {
    const getAccountMenus = _.filter(
      getAllMenus,
      (item) => item.title === "My Account"
    );
    if (getAccountMenus !== undefined) {
      setGetMyAccount(getAccountMenus[0]);
    }
  }, [getAllMenus]);

  const handleOpenNavMenu = () => {
    setMobileOpen(true);
  };

  const handleCloseNavMenu = () => {
    setMobileOpen(false);
  };

  const handlelanguageClick = (event) => {
    setAnchorElLanguage(event.currentTarget);
  };

  const handleLanguageClose = (lang) => {
    setAnchorElLanguage(null);
    if (lang) {
      i18n.changeLanguage(lang);
    }
  };

  const handleProfileClick = (event) => {
    setAnchorElProfile(event.currentTarget);
  };

  const handleProfileClose = () => {
    setAnchorElProfile(null);
  };

  const checkScrollButtons = () => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const scrollLeft = Math.ceil(el.scrollLeft);
    const scrollWidth = Math.ceil(el.scrollWidth);
    const clientWidth = Math.ceil(el.clientWidth);
    setCanScrollLeft(scrollLeft > 0);
    const nearEnd = scrollLeft + clientWidth >= scrollWidth - 1;
    setCanScrollRight(!nearEnd);
  };

  const handleScroll = (direction) => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const scrollAmount = 200;
    el.scrollBy({
      left: direction === "right" ? scrollAmount : -scrollAmount,
      behavior: "smooth",
    });
    setTimeout(() => {
      if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 2) {
        el.scrollLeft = el.scrollWidth;
      }
      checkScrollButtons();
    }, 500);
  };

  const handleUpgradePopupOpen = () => {
    setIsUpgradePopupOpen(true);
  };

  const handleUpgradePopupClose = () => {
    setIsUpgradePopupOpen(false);
  };

  const handleUnreadCountUpdate = useCallback((count) => {
    setUnreadCount(count);
  }, []);

  const handleShowSnackbar = (message, severity) => {
    showSnackbar(message, severity);
  };

  const handleUpgradeClick = () => {
    handleCloseNavMenu();

    const purchaseDetails = getLoggedUserDetails?.PurchasePackageDetails;
    const latestPackage = purchaseDetails?.LatestPackageType;
    const sponsorSite = getLoggedUserDetails?.UserAccountDetails?.DirectSponsorDetails ?.DSReplicationSiteName;

    if (purchaseDetails?.IsBYLDerEnrolled) {
      navigate("/membership");
    } else if (latestPackage?.startsWith("Consultant")) {
      handleUpgradePopupOpen();
    } else if (latestPackage?.startsWith("Aventi Travel Business")) {
      navigate(`/IBOSignup/ATBBYLDerUpgrade/${sponsorSite}`, {
        state: {
          upgrade: true,
          isAventiUpgrade:
            getLoggedUserDetails?.SignupSource === "aventi signup",
        },
      });
    } else if (latestPackage?.startsWith("Aventi Travel")) {
      handleUpgradePopupOpen();
    } else {
      handleUpgradePopupOpen();
    }
  };
  return (
    <>
      <MotionAppBar
        position="static"
        className="cm-header-main"
        animate={isMobile ? { y: 0 } : controls}
        transition={{ duration: 0.5 }}
        initial={{ y: 0 }}
        sx={{ zIndex: 1200 }}
      >
        <Container maxWidth="xxl" className="cm-header-content">
          <Toolbar
            sx={{ flexDirection: { xs: "row", md: "column" }, display: "flex" }}
            disableGutters
          >
            {/* Top Header */}
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"space-between"}
              gap={1}
              width={"100%"}
              sx={{
                borderBottom: { md: "1px solid #ffffff20", xs: "none" },
                paddingBottom: { md: "20px", xs: 0 },
              }}
            >
              <Stack direction="row" gap={3} alignItems="center">
                <Link className="logo-link" to="../home">
                  <img
                    src={`${process.env.REACT_APP_BLOB_URL}/backoffice-assets/logo-header.svg`}
                    alt="logo"
                  />
                </Link>
                <Button
                  sx={{ display: { xs: "none", md: "flex" } }}
                  variant="contained"
                  color="secondary"
                  onClick={handlelanguageClick}
                >
                  {currentLang.toUpperCase()}
                </Button>
                <Menu
                  anchorEl={anchorElLanguage}
                  open={Boolean(anchorElLanguage)}
                  onClose={() => handleLanguageClose()}
                >
                  {Object.entries(locales).map(([lang, { title }]) => (
                    <MenuItem
                      key={lang}
                      selected={lang === currentLang}
                      onClick={() => {
                        handleLanguageClose(lang);
                        handleCloseNavMenu();
                      }}
                    >
                      {title}
                    </MenuItem>
                  ))}
                </Menu>
              </Stack>
              <Stack>
                <Stack
                  sx={{ display: { xs: "none", md: "flex" } }}
                  direction={"row"}
                  alignItems={"center"}
                  gap={2.5}
                >
                  <Stack direction={"row"} alignItems={"center"} gap={1}>
                    <Typography variant="body1" color="secondary.text">
                      Hi{" "}
                      {
                        getLoggedUserDetails?.UserAccountDetails
                          ?.PvtAcctFullName
                      }{" "}
                      ({getLoggedUserDetails?.UserAccountDetails?.AcctID})
                    </Typography>
                    <Button
                      variant="contained"
                      sx={{ px: 0.8, py: 0.5 }}
                      onClick={handleProfileClick}
                      color="secondary"
                    >
                      {getLoggedUserDetails?.UserAccountDetails
                        ?.ProfilePictureURL ? (
                        <img
                          src={
                            getLoggedUserDetails.UserAccountDetails
                              .ProfilePictureURL
                          }
                          alt="user profile"
                          style={{
                            width: "35px",
                            height: "35px",
                            borderRadius: "50%",
                          }}
                        />
                      ) : (
                        <img src="/icons/user-header.svg" alt="user profile" />
                      )}
                    </Button>
                    <Menu
                      anchorEl={anchorElProfile}
                      open={Boolean(anchorElProfile)}
                      onClose={() => handleProfileClose()}
                      className="cm-dropdown"
                    >
                      {getMyAccount !== null &&
                        _.map(getMyAccount?.children, (children, index) => {
                          return (
                            <MenuItem
                              key={index}
                              color={
                                selectedItems.includes(children.id)
                                  ? "primary"
                                  : "inherit"
                              }
                              className={
                                pathname === children.navLink ||
                                selectedSubmenuItems.includes(children.id)
                                  ? "active"
                                  : ""
                              }
                              onClick={() => {
                                handleProfileClose();
                                handleCloseNavMenu();
                                navigate(children.navLink);
                              }}
                            >
                              <svg
                                width={20}
                                height={20}
                                style={{ marginRight: 15 }}
                              >
                                <use href={`#${children.icon}`} />
                              </svg>
                              {children.title}
                            </MenuItem>
                          );
                        })}
                      <Button
                        variant="contained"
                        sx={{
                          px: 3,
                          py: 0.5,
                          width: "100%",
                          background: "var(--black)",
                          border: "1px solid var(--headerbg)",
                          color: "var(--white)",
                          fontWeight: "500",
                        }}
                        disableElevation
                        color="secondary"
                        onClick={() => {
                          store.dispatch(
                            login({
                              data: {
                                name: "",
                                authenticated: false,
                              },
                            })
                          );
                          store.dispatch(
                            userDetail({
                              userDetail: {},
                            })
                          );

                          store.dispatch(
                            menuList({
                              menuList: [],
                            })
                          );

                          store.dispatch(
                            menuItemList({
                              menuItemList: [],
                            })
                          );
                          logUserOut();
                        }}
                      >
                        {t("Common.Sign out")}
                      </Button>
                    </Menu>
                  </Stack>
                  {/* <Button
                    variant="contained"
                    sx={{
                      px: 0.9,
                      py: 0.7,
                    }}
                    color="secondary"
                    onClick={() => setOpen(true)}
                  >
                    <Badge
                      color="primary"
                      badgeContent={unreadCount > 0 ? unreadCount : null}
                    >
                      <NotificationsOutlinedIcon fontSize="large" />
                    </Badge>{" "}
                  </Button>
                  <InAppNotifications
                    open={open}
                    setOpen={setOpen}
                    acctId={getLoggedUserDetails?.UserAccountDetails?.AcctID}
                    onUnreadCountUpdate={handleUnreadCountUpdate}
                    onShowSnackbar={handleShowSnackbar}
                  /> */}
                  {getLoggedUserDetails?.PurchasePackageDetails !== null &&
                  !getLoggedUserDetails?.PurchasePackageDetails
                    ?.IsBYLDerPackagePurchased ? (
                    <Button
                      variant="contained"
                      sx={{ px: 3 }}
                      color="primary"
                      onClick={() => handleUpgradePopupOpen()}
                    >
                      {t("Common.Upgrade")}
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </Stack>
            <Stack sx={{ mt: { xs: 0, md: "10px" } }} className="main-menu">
              {/* Mobile Menu Toggle */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  width: "100%",
                  justifyContent: "flex-end",
                }}
              >
                <IconButton
                  className="mobile-toggle"
                  size="large"
                  aria-label="menu"
                  onClick={handleOpenNavMenu}
                  sx={{
                    padding: "10px !important",
                    background: "var(--primary-main) !important",
                    color: "var(--black) !important",
                    borderRadius: "12px",
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>

              {/* Desktop Menu (Horizontal) */}
              {canScrollLeft && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => handleScroll("left")}
                  sx={{
                    px: 1,
                    py: 1,
                    margin: 0,
                    "& .MuiButton-startIcon": {
                      margin: 0,
                    },
                    display: { xs: "none", md: "flex" },
                  }}
                  startIcon={<WestOutlinedIcon />}
                />
              )}
              <Box
                ref={scrollContainerRef}
                sx={{
                  display: {
                    xs: "none",
                    md: "flex",
                    overflow: "auto",
                    width: "calc(100% - 60px)",
                    scrollbarWidth: "none",
                  },

                width: "100%",
                scrollbarWidth: "none",
                " &::-webkit-scrollbar": {
                  display: "none",
                },
              }}
              style={{ cursor: "grab" }}
            >
              <RecursiveHorizontalMenu
                items={_.filter(
                  getAllMenus,
                  (menus) => menus.title !== "My Account"
                )}
                selectedItems={selectedItems}
              />
            </Box>
            {canScrollRight && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleScroll("right")}
                sx={{
                  px: 1,
                  py: 1,
                  margin: 0,
                  "& .MuiButton-startIcon": {
                    margin: 0,
                  },
                  display: { xs: "none", md: "flex" },
                }}
                startIcon={<EastOutlinedIcon />}
              />
            )}
          </Stack>
        </Toolbar>
      </Container>

      {/* Drawer for Mobile View */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleCloseNavMenu}
        className="cm-menu-drawer"
        PaperProps={{
          sx: (theme) => ({
            width: 280,
            p: "25px 30px",
            background: theme.palette.common.black,
            border: "1px solid var(--headerbg)",
            borderRadius: "20px",
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            height: "100%",
            borderBottom: {
              xs: "1px solid #ffffff20",
              sm: "none",
            },
          }),
        }}
      >
        <Stack gap={2}>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            gap={1}
          >
            <Typography variant="body1" color="secondary.text">
              Hi {getLoggedUserDetails?.UserAccountDetails?.PvtAcctFullName} (
              {getLoggedUserDetails?.UserAccountDetails?.AcctID})
            </Typography>
            <Button
              variant="contained"
              sx={{ px: 0.8, py: 0.5 }}
              onClick={handleProfileClick}
              color="secondary"
            >
              {getLoggedUserDetails?.UserAccountDetails?.ProfilePictureURL ? (
                  <img 
                  src={getLoggedUserDetails.UserAccountDetails.ProfilePictureURL} 
                  alt="user profile" 
                  style={{
                    width: '35px',
                    height: '35px',
                    borderRadius: '50%',
                  }}                 
                />
              ) : (
                <img src="/icons/user-header.svg" alt="user profile" />
              )}
            </Button>
          </Stack>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            gap={1}
          >
            <Button
              sx={{}}
              variant="contained"
              color="secondary"
              onClick={handlelanguageClick}
            >
              {currentLang.toUpperCase()}
            </Button>
            {/* <Button
              variant="contained"
              sx={{
                px: 0.9,
                py: 0.7,
              }}
              color="secondary"
              onClick={() => setOpen(true)}
            >
              <Badge
                color="primary"
                badgeContent={unreadCount > 0 ? unreadCount : null}
              >
                <NotificationsOutlinedIcon fontSize="large" />
              </Badge>{" "}
            </Button> */}
            </Stack>
            {getLoggedUserDetails?.PurchasePackageDetails !== null &&
            !getLoggedUserDetails?.PurchasePackageDetails
              ?.IsBYLDerPackagePurchased ? (
              <Button
                variant="contained"
                sx={{ px: 3 }}
                color="primary"
                onClick={() => handleUpgradePopupOpen()}
              >
                {t("Common.Upgrade")}
              </Button>
            ): null}
          </Stack>
          <RecursiveHorizontalMenu
            items={_.filter(
              getAllMenus,
              (menus) => menus.title !== "My Account"
          )}
          selectedItems={selectedItems}
          isMobile={true}
          handleCloseNavMenu={handleCloseNavMenu}
        />
      </Drawer>

        {/* Upgrade Popup */}
        <Dialog
          sx={{ borderRadius: "20px", background: "transparent" }}
          open={isUpgradePopupOpen}
          maxWidth
        >
          <Stack
            sx={(theme) => ({
              background: theme.palette.common.black,
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
            })}
          >
            <DialogTitle>{t("Common.SignUp")}</DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleUpgradePopupClose}
              sx={(theme) => ({
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
              })}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <DialogContent
            sx={{
              background: "var(--primary-darker)!important",
              border: 0,
              borderBottomLeftRadius: "20px",
              borderBottomRightRadius: "20px",
            }}
            dividers
          >
            <Stack direction={"row"} flexWrap={"wrap"} gap={2}>
              {getLoggedUserDetails?.UpgradePackageDetails?.map(
                (item, index) => {
                  return (
                    <Card
                      key={index}
                      sx={(theme) => ({
                        p: "20px",
                        backgroundImage: theme.palette.background.cardGradient,
                        border: "1px solid var(--white)",
                        borderRadius: "20px",
                        display: "flex",
                        justifyContent: "center",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "10px",
                        flex: "1",
                        maxWidth: { xs: "100%", sm: "250px" },
                        minWidth: { xs: "200px", sm: "250px" },
                      })}
                    >
                      <Stack alignItems={"center"} gap={"10px"}>
                        <Card
                          sx={(theme) => ({
                            p: "10px",
                            background: theme.palette.secondary.light,
                            borderRadius: "5px",
                            display: "flex",
                            justifyContent: "center",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: "10px",
                            padding: "5px 10px",
                            color: theme.palette.common.black,
                            backgroundImage: "none",
                            fontWeight: "600",
                            fontSize: "16px",
                          })}
                        >
                          {getLoggedUserDetails?.SignupSource === "aventi signup" ? item.PackageName : item.HardCodeName}
                        </Card>
                        <img width={80} src="/icons/badge1.svg" alt="user" />
                        <Typography
                          color="secondary.light"
                          variant="h5"
                          fontWeight={600}
                        >
                          ${item.PackagePrice} {t("Common.One-Time")}
                        </Typography>
                        <Typography
                          color="secondary.text"
                          sx={{ fontSize: "12px" }}
                          variant="body1"
                          fontWeight={400}
                          lineHeight={1.7}
                        >
                          (${item.MonthlyPrice} {t("Common.Monthly")})
                        </Typography>
                        <Button
                          variant="contained"
                          sx={{ px: 3 }}
                          color="primary"
                          onClick={() => {
                            navigate(
                              `${item.RedirectUrl}${getLoggedUserDetails?.UserAccountDetails?.DirectSponsorDetails?.DSReplicationSiteName}`,
                              { state: { upgrade: true, isAventiUpgrade: getLoggedUserDetails?.SignupSource === "aventi signup" } }
                            );
                          }}
                        >
                          {t("Common.BUY PACKAGE")}
                        </Button>
                      </Stack>
                    </Card>
                  );
                }
              )}
            </Stack>
            <Stack alignItems={"center"} mt={"20px"} textAlign={"center"}>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: "var(--red)",
                  color: "var(--white)",
                  px: 3,
                  fontWeight: 600,
                }}
                color="primary"
                startIcon={<CloseIcon color="primary" />}
                onClick={handleUpgradePopupClose}
              >
                {t("Common.Cancel")}
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>

        {impersonateEmail && (
          <Box sx={{ alignItems: "center", mt: 1 }}>
            <Alert
              sx={(theme) => ({
                background: theme.palette.common.errorbg,
                color: theme.palette.common.errortext,
              })}
              variant="filled"
              severity="info"
            >
              You are logged in as impersonate user!
            </Alert>
          </Box>
        )}
      </MotionAppBar>
      <CommonSnackBar snackObj={snack} closeSnack={closeSnack} buttonProp />
    </>
  );
}

const RecursiveHorizontalMenu = ({
  items = [],
  selectedItems = [],
  level = 0,
  isMobile = false,
  handleCloseNavMenu,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openId, setOpenId] = useState(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setOpenId(id);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpenId(null);
  };

  return (
    <Box
      display="flex"
      sx={{ flexDirection: { xs: "column", md: "row" }, mt: { xs: 2, md: 0 } }}
      gap={"10px"}
      alignItems={"center"}
    >
      {items.map((item) => {
        const hasChildren = item.children?.length || item.InnerChildren?.length;
        const children = item.children || item.InnerChildren;

        return (
          <Box sx={{ width: "100%" }} key={item.id}>
            {item.type === "item" || item.type === "external-link" ? (
              <Button
                onClick={() => {
                  if (item.type === "external-link") {
                    window.open(item.navLink, "_blank");
                  } else {
                    isMobile && handleCloseNavMenu();
                    if (item.navLink !== "/mediaPage") {
                      navigate(item.navLink);
                    } else {
                      const encodedMedia = btoa(item.id);
                      navigate(`${item.navLink}/${encodedMedia}`);
                    }
                  }
                }}
                color={selectedItems.includes(item.id) ? "primary" : "inherit"}
                sx={{ textTransform: "none" }}
                className={`${
                  selectedItems.includes(item.id) ? "active" : ""
                } ${item.navLink === "/mediaPage" ? "media-page" : ""}`}
              >
                <svg width={20} height={20}>
                  <use href={`#${item.icon}`} />
                </svg>
                {item.title !== "Home" && t(item.title)}
              </Button>
            ) : (
              <>
                <Button
                  onClick={(e) => {
                    if (item.navLink === "/mediaPage") {
                      const encodedMedia = btoa(item.id);
                      navigate(`${item.navLink}/${encodedMedia}`);
                      isMobile && handleCloseNavMenu();
                    } else {
                      handleOpen(e, item.id);
                    }
                  }}
                  endIcon={<ArrowDropDownIcon />}
                  color={
                    selectedItems.includes(item.id) ? "primary" : "inherit"
                  }
                  className={`${
                    selectedItems.includes(item.id) ? "active" : ""
                  } ${item.navLink === "/mediaPage" ? "media-page" : ""}`}
                  sx={{ textTransform: "none" }}
                >
                  <svg width={20} height={20}>
                    <use href={`#${item.icon}`} />
                  </svg>
                  {item.title}
                </Button>
                {item.navLink !== "/mediaPage" && hasChildren && (
                  <Menu
                    anchorEl={anchorEl}
                    open={openId === item.id}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    keepMounted
                  >
                    <RecursiveMenuItems
                      items={children}
                      level={level + 1}
                      onClose={handleClose}
                      isMobile={isMobile}
                      handleCloseNavMenu={handleCloseNavMenu}
                      selectedItems={selectedItems}
                    />
                  </Menu>
                )}
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const RecursiveMenuItems = ({
  items,
  level,
  onClose,
  isMobile,
  handleCloseNavMenu,
  selectedItems = [],
}) => {
  const [subAnchorEl, setSubAnchorEl] = useState(null);
  const [subOpenId, setSubOpenId] = useState(null);
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();
  const handleSubOpen = (event, id) => {
    setSubAnchorEl(event.currentTarget);
    setSubOpenId(id);
  };

  const handleSubClose = () => {
    setSubAnchorEl(null);
    setSubOpenId(null);
    onClose();
  };

  return (
    <>
      {items.map((item) => {
        const hasChildren = item.children?.length || item.InnerChildren?.length;
        const children = item.children || item.InnerChildren;

        if (hasChildren) {
          return (
            <div key={item.id}>
              <MenuItem
                onClick={(e) => handleSubOpen(e, item.id)}
                color={selectedItems.includes(item.id) ? "primary" : "inherit"}
              >
                <svg width={20} height={20}>
                  <use href={`#${item.icon}`} />
                </svg>
                {item.title}
                <ArrowDropDownIcon style={{ marginLeft: "auto" }} />
              </MenuItem>
              <Menu
                anchorEl={subAnchorEl}
                open={subOpenId === item.id}
                onClose={handleSubClose}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <RecursiveMenuItems
                  items={children}
                  level={level + 1}
                  onClose={handleSubClose}
                  isMobile={isMobile}
                  handleCloseNavMenu={handleCloseNavMenu}
                />
              </Menu>
            </div>
          );
        }

        return (
          <MenuItem
            key={item.id}
            className={pathname === item.navLink ? "active" : ""}
            onClick={() => {
              handleSubClose();
              if (item.type === "external-link") {
                window.open(item.navLink, "_blank");
              } else {
                isMobile && handleCloseNavMenu();
                if (item.navLink !== "/mediaPage") {
                  navigate(item.navLink);
                } else {
                  const encodedMedia = btoa(item.id);
                  navigate(`${item.navLink}/${encodedMedia}`);
                }
              }
            }}
          >
            <svg width={20} height={20} style={{ marginRight: 15 }}>
              <use href={`#${item.icon}`} />
            </svg>
            {item.title}
          </MenuItem>
        );
      })}
    </>
  );
};

export default Header;